import React, { useEffect, useState, useMemo } from "react";
import { IHomeTicket, ITicket, ITicketListItem } from "../types/Ticket/Ticket";
import { userApi } from "../redux/api/userApi";
import { Grid, GridCellsSettings, GridColumn, GridColumnMenuColumnsChooser, GridColumnsStateChangeEvent } from "@progress/kendo-react-grid";
import { useTabConfig } from "./hooks/useTabConfig";
import { setColumns, setGroupSettings } from "../redux/slices/tabSlice";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks/hooks";
import { accountApi } from "../redux/api/accountApi";
import { CustomRowFormatter } from "./CustomRowFormatter";
import './TicketGrid.scss';
import { selectTenantUrl } from "../redux/selectors/authSelector";
import { defaultColumns } from "../settings/defaultColumns";
import IGridState, { IColumn } from "../types/User/IGridState";
import { ISetting } from "../types/User/ISetting";

type Props = {
    onOpenTicket?: (ticket: ITicket) => void;
    pageSize?: number;
    list: ITicketListItem[];
};

const TicketGrid: React.FC<Props> = ({ list, onOpenTicket, pageSize = 50 }) => {
    const dispatch = useReduxDispatch();
    const tabConfig = useTabConfig();
    const tenantUrl = useReduxSelector(selectTenantUrl);
    const [saveSetting] = userApi.useSaveSettingMutation();
    const { data: gridState } = userApi.useGetSettingQuery(146);
    const { data: profile } = userApi.useGetProfileQuery();
    const { data: hostConfig } = accountApi.useGetHostConfigQuery(profile?.Tenant_Uid || "", { skip: !profile?.Tenant_Uid });
    //const { data: tickets, isLoading: myTicketsLoading } = ticketApi.useGetMyTicketsQuery({ contactId: profile?.Id }, { skip: !profile?.Id || !tabConfig.myCallsOnly, pollingInterval: 60000 });
    //const { data: allOpenTickets, isLoading: isLoadingAllOpenTickets } = ticketApi.useGetTicketListQuery({ myTicketsOnly: tabConfig.myCallsOnly, companyIds: tabConfig.clientIds, assignedToIds: tabConfig.queueIds.map(String), assignedTeamIds: tabConfig.teamIds, priorityIds: tabConfig.priorityIds } as OpenTicketProps, { pollingInterval: 60000 });
    const [filter, setFilter] = useState<string>("");
    const [items, setItems] = useState<IHomeTicket[]>([]);
    //const isLoading = false || isLoadingAllOpenTickets;
    const { columns, groupSettings } = useReduxSelector((state) => state.tab);

    //load the column settings into the redux state
    useEffect(() => {

        //only load the grid dsettings if we have a profile and the columns haven't already been loaded (from another tab for example)
        if (profile && !columns && gridState) {
            try {
                console.log("TRY Loading grid state:", gridState, profile, columns);

                if (gridState?.Big_String_Value) {
                    const state = JSON.parse(gridState.Big_String_Value) as IGridState
                    console.log("Loaded grid state columns:", state.columns, state.groups);
                    state.columns && dispatch(setColumns(state.columns.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))));
                    dispatch(setGroupSettings(state.groups));
                } else {
                    //const cols: IColumn[] = JSON.parse(gridState.Big_String_Value)?.columns as IColumn[];
                    //const groups = JSON.parse(gridState.Big_String_Value)?.groups as GroupDescriptor[];

                    dispatch(setColumns(defaultColumns(profile!)));
                }
                //dispatch(setGroupSettings(groups));
            } catch (e) {
                console.error("Failed to parse grid state columns:", e);
            }
        }
    }, [gridState, dispatch, profile, columns]);




    //console.log('grid settings:', JSON.parse(gridState?.Big_String_Value ?? "{}"))



    function isValidDate(value: unknown): value is Date {
        // Check if it is an instance of Date and if its internal timestamp is not NaN
        return value instanceof Date && !isNaN(+value);
    }


    const getCellSettings = (field: string): GridCellsSettings => {
        if (field === "Id") {
            return {
                data: (props: any) => {
                    const dataItem = props.dataItem as ITicketListItem;
                    return (
                        <td className="cidc">
                            <a href={`${tenantUrl}/Calls/CallHistory.aspx?Call_Id=${dataItem.Id}&from=Teams`} target="_blank" rel="noopener noreferrer">#{dataItem.Id}</a>
                        </td>
                    );
                }
            } as GridCellsSettings
        }
        else if (field === "Call_Desc") {
            return {
                data: (props: any) => {
                    const dataItem = props.dataItem as ITicketListItem;
                    return (
                        <td>
                            <div dangerouslySetInnerHTML={{ __html: dataItem.Call_Desc }}></div>
                        </td>
                    );
                }
            } as GridCellsSettings
        }
        else if (field === "Open_Dt") {
            return {
                data: (props: any) => {
                    const dataItem = props.dataItem as ITicketListItem;
                    return (
                        <td>
                            <div>{new Date(dataItem.Open_Dt + 'Z').toLocaleDateString()}</div>
                            {new Date(dataItem.Open_Dt + 'Z').toLocaleTimeString()}
                        </td>
                    );
                }
            } as GridCellsSettings
        } else if (field === "Due_Dt") {
            return {
                data: (props: any) => {
                    const dataItem = props.dataItem as ITicketListItem;
                    return (
                        !isValidDate(new Date(dataItem.Due_Dt + 'Z')) ? <td></td> :
                            <td>
                                <div>{new Date(dataItem.Due_Dt + 'Z').toLocaleDateString()}</div>
                                {new Date(dataItem.Due_Dt + 'Z').toLocaleTimeString()}
                            </td>
                    );
                }
            } as GridCellsSettings
        } else if (field === "Assigned_Staff") {
            return {
                data: (props: any) => {
                    const dataItem = props.dataItem as ITicketListItem;
                    const assignedStaffArr = dataItem.Assigned_Staff.map(t => t.Name);
                    return (
                        <td>
                            {assignedStaffArr.map((staff, index) => (
                                <div key={index}>{staff?.replace('<phrase>Queue</phrase>', profile?.Phrases?.Queue || "Queue")}</div>
                            ))}

                        </td>
                    );
                }
            } as GridCellsSettings
        }
        else {
            return {};
        }
    };

    // useEffect(() => {
    //     console.log("groupSettings changed:", groupSettings?.length);
    //     //If the group settings chnage
    // }   , [groupSettings]);

    const columnStateChanged = (e: GridColumnsStateChangeEvent) => {
        console.log("Column state changing:", e.columnsState);
        //Take the defaulty columns and override just the order and width of each column
        const defaultCols = defaultColumns(profile!);
        const mergedState = defaultCols.map(dc => {
            const updatedCol = e.columnsState.find(c => c.field === dc.field);
            const colhidden = columns?.find(c => c.field === dc.field)?.hidden || false;
            return updatedCol ? { ...dc, width: updatedCol.width, orderIndex: updatedCol.orderIndex, hidden: colhidden } as IColumn : null;
        }).filter((col): col is IColumn => (col !== null));

        const gridState: IGridState = { columns: mergedState, groups: groupSettings };

        console.log("Column state changed:", gridState);

        //Update column state via the API.
        saveSetting({ Setting_Id: 146, Big_String_Value: JSON.stringify(gridState) } as ISetting).unwrap()
            .then(() => {
                console.log("Grid state saved successfully");
            })
            .catch((error) => {
                console.error("Failed to save grid state:", error);
            });

        // Save the new column state to the server or local storage
        // For example, using an API call:
        // userApi.saveGridState(gridState);
    }

    const transformedList = useMemo(() => {
        if (!list) return [];
        return list.flatMap((item) => {
            const assigned = (item.Assigned_Staff && item.Assigned_Staff.length) ? item.Assigned_Staff : [{ Name: "Unassigned" } as any];
            return assigned.map(staff => ({ ...item, Assigned_Staff_Group: staff.Name } as any));
        });
    }, [list]);

    const effectiveGroup = useMemo(() => {
        if (!groupSettings) return groupSettings;
        return groupSettings.map(g => g.field === "Assigned_Group" ? { ...g, field: "Assigned_Staff_Group" } : g);
    }, [groupSettings]);

    const isGroupingByAssigned = useMemo(() => {
        return Array.isArray(effectiveGroup) && effectiveGroup.some(g => g.field === "Assigned_Staff_Group");
    }, [effectiveGroup]);

    const dataForGrid = isGroupingByAssigned ? transformedList : (list || []);

    //return <>{JSON.stringify({ transformedList, effectiveGroup, isGroupingByAssigned })}</>

    return (
        <>
            {/* allOpenTickets length = {allOpenTickets?.length}<br />
             */}
            {/* {window.document.location.href}<br /> */}
            <Grid data={dataForGrid || []}
                groupable={true}
                sortable={true}
                defaultSort={[{ field: "Id", dir: "desc" }]}
                defaultTake={50}
                pageable={true}
                group={effectiveGroup}
                style={{ display: 'contents' }}
                rows={CustomRowFormatter(dataForGrid || [], profile!, hostConfig!)}
                autoProcessData={true}
                reorderable={true}
                onColumnsStateChange={columnStateChanged}
            >
                {columns?.map((col: IColumn) => (
                    <GridColumn key={col.field} field={col.field} title={col.title} hidden={col.hidden} width={col.width}
                        cells={getCellSettings(col.field)}
                    />
                ))}

            </Grid>

        </>
    );
};

export default TicketGrid;